import { SxProps, Theme } from "@mui/material";
import { getContrastColor } from "theme/utils";

const getStyles = (theme: Theme): Record<string, Record<string, SxProps<Theme>>> => ({
  Header: {
    container: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "var(--rhythm)",
      // maxWidth: "1440px",
      mb: "var(--rhythm)",
      [theme.breakpoints.down("md")]: {
        flexWrap: "wrap",
      },
    },

    alert: {
      boxSizing: "border-box",
      flex: "0 1 auto",
      display: "flex",
      alignItems: "center",
      gap: "var(--rhythm)",
      minHeight: "calc(3 * var(--rhythm))",
      padding: "calc(0.5 * var(--rhythm))",
      ".MuiAlert-message": {
        display: "flex",
        alignItems: "center",
        gap: "var(--rhythm)",
        p: 0,
        overflow: "hidden",
      },
      [theme.breakpoints.down("md")]: {
        borderLeft: "none",
        borderRight: "none",
        borderRadius: 0,
      },
    },

    link: {
      color: "var(--color-black)",
      background: "var(--color-emerald)",
      "&:hover": { background: "var(--color-emerald-dark)" },
    },

    button: {
      flex: "0 0 auto",
      minWidth: "300px",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
  },

  Search: {
    container: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "start",
      maxHeight: "calc(3 * var(--rhythm))",
      mb: "var(--rhythm)",
      [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        maxHeight: "none",
      },
    },

    heading: {
      flex: "0 0 auto",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "start",
      width: "400px",
      [theme.breakpoints.down("md")]: {
        alignItems: "center",
        mb: "var(--rhythm)",
      },
    },

    search: {
      flex: "1 1 auto",
      alignItems: "center",
      justifyContent: "center",
      maxWidth: "480px",
      [theme.breakpoints.down("md")]: {
        minWidth: "100%",
      },
    },
  },

  Filters: {
    container: {
      flexDirection: "column",
      justifyContent: "start",
      marginBottom: "var(--rhythm)",
      [theme.breakpoints.down("md")]: {},
    },

    group: {
      flexDirection: "row",
      alignItems: "flex-start",
      minHeight: "calc(3 * var(--rhythm))",
      [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        height: "auto",
      },
    },

    label: {
      minWidth: "400px",
      mt: "calc(1 * var(--rhythm))",
      [theme.breakpoints.down("md")]: {
        mb: "calc(0.5 * var(--rhythm))",
      },
    },

    select: {
      width: "480px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },

    slider: {
      width: "460px",
      marginX: "calc(0.5 * var(--rhythm))",
      mt: "15px",
      [theme.breakpoints.down("md")]: {
        width: "calc(100% - var(--rhythm))",
        mt: 0,
      },
    },

    chekcboxes: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      width: "480px",
      mt: "8px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
        mt: 0,
      },
    },

    chips: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "flex-start",
      gap: "calc(0.5 * var(--rhythm))",
      width: "480px",
      mt: "15px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
        mt: 0,
      },
    },
  },

  Card: {
    container: {
      display: "flex",
      flexDirection: "column",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "flex-start",
      width: "400px",
      minHeight: "calc(15 * var(--rhythm))",
      [theme.breakpoints.down("md")]: {
        width: "calc(50% - var(--rhythm))",
      },
      "@media screen and (min-width: 1230px)": {
        width: "260px",
      },
      "@media screen and (min-width: 1316px)": {
        width: "290px",
      },
      "@media screen and (min-width: 1390px)": {
        width: "315px",
      },
      "@media screen and (min-width: 1870px)": {
        width: "340px",
      },
      "@media screen and (min-width: 2510px)": {
        width: "380px",
      },
    },

    image: {
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "calc(7 * var(--rhythm))",
      boxSizing: "border-box",
      borderBottom: "1px solid var(--color-border)",
      img: {
        width: "100%",
        height: "100%",
        margin: 0,
        padding: "var(--rhythm)",
        objectFit: "contain",
        objectPosition: "center center",
        boxSizing: "border-box",
      },
    },

    family: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "calc(3 * var(--rhythm))",
      textAlign: "center",
      wordBreak: "break-all",
      boxSizing: "border-box",
      overflow: "hidden",
      borderBottom: "1px solid var(--color-border)",
    },

    section: {
      flex: "1 1 auto",
      flexDirection: "column",
      justifyContent: "flex-end",
      width: "100%",
    },

    description: {
      width: "100%",
      padding: "var(--rhythm)",
      boxSizing: "border-box",
      fontSize: "var(--font-size-xs)",
      lineHeight: "var(--base-line-height)",
      textAlign: "center",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },

    tooltip: {
      height: "var(--rhythm)",
      mb: "var(--rhythm)",
      textAlign: "center",
    },

    control: {
      flexDirection: "row",
      width: "100%",
    },

    checkbox: {
      ml: "calc(0.5 * var(--rhythm))",
      height: "calc(2.5 * var(--rhythm))",
      ".MuiCheckbox-root": {
        width: "calc(1.5 * var(--rhythm))",
        height: "calc(1.5 * var(--rhythm))",
        border: "1px solid var(--color-grey-light)",
        borderRadius: 0,
        "&::before": {
          width: "var(--rhythm)",
          height: "var(--rhythm)",
          background: "var(--color-grey-light)",
          borderColor: "var(--color-grey-light)",
          borderRadius: 0,
        },
        "&.Mui-checked": {
          borderColor: getContrastColor(),
          "&::before": {
            background: getContrastColor(),
            borderColor: getContrastColor(),
          },
        },
      },
      ".MuiCheckbox-root.Mui-checked + .MuiFormControlLabel-label": {
        color: getContrastColor(),
      },
      ".MuiFormControlLabel-label": {
        ml: "calc(0.5 * var(--rhythm))",
        fontSize: "var(--font-size-s)",
        fontWeight: "bold",
        color: "var(--color-grey-dark)",
      },
      ".MuiSvgIcon-root": {
        display: "none",
      },
    },
  },
});

export default getStyles;
